// Online mode: the server simulates, this tab sends inputs and draws what NetClient computes.
// Between matches a lobby panel shows members and settings (Design §9).
import { NetClient, NetEvent } from '../pkg/client_wasm.js';
import { ask, el, patch } from './dom.js';
import { profile, session, token } from './identity.js';
import { onPadEmote, readAnyInput, takePress } from './input.js';
import { isTouchMode, onTouchEmote, setTouchActive, setTouchEmotes, setTouchRight } from './touch.js';
import { backToMenu } from './menu.js';
import { COLOR, Effects, LOOK_SLOTS, PAINT, POSE } from './looks.js';
import { playerMark } from './colors.js';
import { POSE_H, POSE_TANK_Y, POSE_W, drawPose } from './poses.js';
import { type Constants, TEAM_COLORS, TEAM_NAMES, draw, itemInfo, playerColor, scorePopup, showChallengeDone } from './render.js';
import { CHALLENGE_REWARD } from './challenge.js';
import { renderShop } from './shop.js';
import { countdownSound, sound, soundButton } from './sound.js';
import { STANDARD_KEYS, emoteButton, showsEmotesOf } from './emotes.js';
import { FPS_CELL, fpsMeter, keys, statusBar } from './status.js';

const PING_INTERVAL_MS = 1000;
const RECONNECT_DELAY_MS = 2000;
const MAX_PLAYERS = 16;
const MAX_ROUNDS = 15;
const MAX_WINS = 5; // protocol::MAX_WINS
const VERSION_RELOAD = 'webtanks-version-reload'; // sessionStorage: last reload for a new version
const STANDINGS_GAP = 8; // CSS px at least on both sides of the standings
/** Windows smaller than this (CSS px) show only the map during a match round, as touch screens do (Wunsch User 2026-10-26). */
const FILL_BELOW = { width: 1280, height: 800 };
const STANDINGS_TOP = 16; // CSS px below the top of the map
const PANEL_GAP = 16; // CSS px right of the lobby settings, as in the CSS
const POSE_PX = 120; // CSS width of the victory pose behind the name

/** `code` empty: create a new lobby. */
export function startOnline(
  ctx: CanvasRenderingContext2D,
  status: HTMLElement,
  consts: Constants,
  dt: number,
  code: string,
): void {
  const net = new NetClient();
  const effects = new Effects();
  status.hidden = false; // the start menu hides it
  const board = document.getElementById('board')!;
  const panel = document.getElementById('lobby')!;
  const shop = document.getElementById('shop')!;
  const standings = document.getElementById('standings')!;
  let shopTimer: HTMLElement | undefined; // seconds left, while the shop is open
  let resultHeard = ''; // round and match whose result sound played
  const leave = el('button', { textContent: 'Leave' });
  // Leave and sound at the left end (Wunsch User 2026-10-19).
  const [, , , , , setItem, setPing, setFps, setRound] = statusBar(status, [
    { label: '', width: '130px', content: [leave] },
    { label: '', width: '44px', content: [soundButton()] },
    { label: '', width: '44px', content: [emoteButton()] },
    { label: 'Move', width: '260px', wide: true, content: [...keys('W', 'A', 'S', 'D'), ' / ', ...keys('↑', '←', '↓', '→')] },
    { label: 'Fire', width: '80px', wide: true, content: keys('␣', '⏎') },
    { label: 'Power-up', width: '290px', content: [...keys('E', '⇧'), ' '] },
    { label: 'Ping', width: '80px' },
    FPS_CELL,
    { label: 'Round', width: 'minmax(160px, 1fr)' },
  ]);
  const countFrame = fpsMeter(setFps);
  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  let ws: WebSocket | undefined;
  let walls: Float32Array = new Float32Array();
  let terrain: Float32Array = new Float32Array(); // ground patches, static for the round but for a ghost's piece
  let terrainStamp = 0;
  let closed = false; // rejected: don't reconnect
  let connection = 'Connecting…'; // empty while connected

  const isOpen = () => ws?.readyState === WebSocket.OPEN;
  const send = (bytes: Uint8Array) => {
    if (isOpen()) ws!.send(bytes as Uint8Array<ArrayBuffer>); // wasm memory is never shared
  };

  function connect(): void {
    ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      connection = '';
      send(net.hello(code, token(), session()));
    };
    ws.onmessage = (e) => onMessage(net.on_message(new Uint8Array(e.data as ArrayBuffer), performance.now()));
    ws.onclose = () => {
      if (closed) return;
      connection = 'Disconnected – reconnecting…';
      setTimeout(connect, RECONNECT_DELAY_MS);
    };
  }

  function onMessage(ev: NetEvent): void {
    if (ev !== NetEvent.None) tankData = readTankData();
    if (ev === NetEvent.Standings) showGains();
    if (ev === NetEvent.Challenge) showChallenges();
    if ([NetEvent.Round, NetEvent.RoundOver, NetEvent.Shop, NetEvent.Standings, NetEvent.Lobby].includes(ev)) {
      showSide();
    }
    switch (ev) {
      case NetEvent.Round:
        walls = net.walls();
        terrain = net.terrain_patches();
        terrainStamp = net.terrain_stamp();
        if (net.round_number() > 0) board.hidden = true; // the final standings stay over the warm-up
        shop.hidden = true;
        shopTimer = undefined;
        break;
      case NetEvent.Shop: {
        board.hidden = true; // the shop shows the points
        shopTimer = renderShop(shop, net, send, tankName, tankColor);
        shop.hidden = false;
        break;
      }
      case NetEvent.MatchOver:
        // Also cut short, with the shop open (someone joined a match against bots).
        shop.hidden = true;
        shopTimer = undefined;
        if (net.round_number() > 0) showBoard();
        break;
      case NetEvent.RoundOver:
        if (net.round_number() > 0) showBoard();
        break;
      case NetEvent.Lobby:
        if (code !== net.lobby_code()) {
          code = net.lobby_code();
          history.replaceState(null, '', `?lobby=${code}`); // reload and shared links rejoin
        }
        showPanel();
        break;
      case NetEvent.Rejected:
        closed = true;
        onRejected(net.rejected_reason());
        break;
    }
  }

  function onRejected(reason: string): void {
    switch (reason) {
      case 'version': {
        // The server was updated: load the new client, but only once a minute (no reload loop
        // if the files still are old, e.g. a cache in between).
        const last = Number(sessionStorage.getItem(VERSION_RELOAD) ?? 0);
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(VERSION_RELOAD, String(Date.now()));
          location.reload();
          return;
        }
        connection = 'New version – please reload the page (Ctrl+F5)';
        break;
      }
      case 'replaced':
        document.getElementById('replaced')!.hidden = false; // covers everything, nothing to do here any more
        return;
      case 'not_found':
        return backToMenu(`Lobby ${code} does not exist (anymore).`);
      case 'lobby_full':
        return backToMenu(`Lobby ${code} is full.`);
      case 'server_full':
        return backToMenu('The server is full – try again later.');
      case 'kicked':
        return backToMenu('You were removed from the lobby.');
      case 'no_guests':
        return backToMenu(`Lobby ${code} is for players with an account only.`);
    }
    panel.hidden = true;
  }

  /** Per-tank data from the lobby, round and shop messages, read again only when one comes (not per frame). */
  function readTankData() {
    return {
      names: net.tank_names(),
      colors: net.tank_colors(),
      teams: net.tank_teams(),
      ranks: net.tank_ranks(),
      looks: net.looks(),
      loadouts: net.loadouts(),
      anonymous: net.anonymous_setting(),
      fog: net.fog_setting(),
    };
  }
  let tankData = readTankData();

  /** Name of the player in tank slot `i`. */
  const tankName = (i: number) => tankData.names[i] || `Player ${i + 1}`;
  const tankColor = (i: number) => playerColor(tankData.colors[i] ?? i);
  /** Colour look's square, else the colour dot (anonymous: the server sends no look). */
  const tankMark = (i: number) => playerMark(tankColor(i), net.looks()[i * LOOK_SLOTS + COLOR] ?? 0);

  /** Points the own tank just earned or lost, rising over it (Wunsch User 2026-10-19). */
  function showGains(): void {
    const gains = net.take_gains();
    for (let k = 0; k < gains.length; k += 4) {
      const [tank, points, why, of] = [gains[k], gains[k + 1], gains[k + 2], gains[k + 3]];
      if (tank !== net.you()) continue;
      const style = why === 1 ? 'bounty' : why === 2 ? 'own' : 'kill';
      // The one killed in its colour; with teams in its team's.
      const color = of < 0 ? '#ffffff' : tankData.teams.length ? TEAM_COLORS[tankData.teams[of]] : tankColor(of);
      scorePopup(tank, points > 0 ? `+${points}` : `−${-points}`, style, color);
    }
  }

  /**
   * Daily challenge done (Wunsch User 2026-10-28): the reward rises over the tank for everybody;
   * its player also gets a banner and a fanfare.
   */
  function showChallenges(): void {
    for (const tank of net.take_challenges()) {
      scorePopup(tank, `+${CHALLENGE_REWARD} ★`, 'challenge', '#ffd54a');
      if (tank !== net.you()) continue;
      showChallengeDone();
      sound('challenge');
    }
  }

  /** Left of the map: the members between matches, the standings during one. */
  function showSide(): void {
    if (net.playing()) showStandings();
    else showMembers();
  }

  /** Member `i` with its badges; the host, an admin or a moderator can kick it. */
  function memberRow(i: number): HTMLElement {
    const me = net.player();
    const id = net.member_id(i);
    const bot = net.member_bot(i);
    const badges = [bot ? 'Bot' : net.member_guest(i) && 'Guest', id === net.host() && 'Host', id === me && 'you'].filter(Boolean);
    return el(
      'div',
      { className: id === me ? 'row you' : 'row' },
      el('span', { className: 'name' }, net.member_slot(i) >= 0 ? playerMark(playerColor(net.member_color(i)), net.member_paint(i)) : null, net.member_name(i)),
      ...badges.map((b) => el('span', { className: 'badge', textContent: String(b) })),
      (net.is_host() || net.is_staff()) &&
        id !== me &&
        !bot &&
        el('button', { className: 'kick', textContent: '✕', title: 'Kick', onclick: () => send(net.kick(id)) }),
      // Last, so the team buttons of people and bots line up (Wunsch User 2026-10-23).
      net.is_host() &&
        teams > 0 &&
        net.member_slot(i) >= 0 &&
        el('button', { className: 'move', textContent: '⇄', title: 'To the next team', onclick: () => send(net.team(id, (net.member_team(i) + 1) % teams)) }),
    );
  }

  /** Members with (`seated`) or without a seat, in member order; only of `team` if given. */
  function members(seated: boolean, team = -1): HTMLElement[] {
    const rows: HTMLElement[] = [];
    for (let i = 0; i < net.member_count(); i++) {
      if ((net.member_slot(i) >= 0) === seated && (team < 0 || net.member_team(i) === team)) rows.push(memberRow(i));
    }
    return rows;
  }

  /** Heading of team `t` in its colour, `extra` after the name. */
  const teamHead = (t: number, extra: string, ...after: (HTMLElement | false)[]) => {
    const head = el('h4', { className: 'team' }, el('span', { textContent: extra ? `${TEAM_NAMES[t]} ${extra}` : TEAM_NAMES[t] }), ...after);
    head.style.color = TEAM_COLORS[t];
    return head;
  };

  /** The seated members by team (Design §12): join buttons, and the host's ways to split them. */
  function teamRows(): HTMLElement[] {
    const me = net.player();
    let mine = -1;
    for (let i = 0; i < net.member_count(); i++) if (net.member_id(i) === me && net.member_slot(i) >= 0) mine = net.member_team(i);
    const split = (how: number, text: string, title: string) => el('button', { textContent: text, title, onclick: () => send(net.split(how)) });
    const tools = net.is_host()
      ? [
          el(
            'div',
            { className: 'split' },
            split(0, 'Shuffle', 'Random teams of equal size'),
            split(1, 'People vs bots', 'All people in one team, the bots in the others'),
            split(2, 'Balance', 'Teams as even as can be by team rating'),
          ),
        ]
      : [];
    return [
      ...tools,
      ...Array.from({ length: teams }, (_, t) => {
        const rows = members(true, t);
        const join = mine >= 0 && mine !== t && el('button', { className: 'join', textContent: 'Join', onclick: () => send(net.team(me, t)) });
        return [teamHead(t, String(rows.length), join), ...rows];
      }).flat(),
    ];
  }

  /** Teams of the lobby: 0 = everyone for himself. */
  let teams = net.teams_setting();

  /** The spectators under a heading, nothing without any. */
  function spectators(): HTMLElement[] {
    const rows = members(false);
    return rows.length > 0 ? [el('h4', { textContent: 'Spectators' }), ...rows] : [];
  }

  /** Players and spectators of the lobby. */
  function showMembers(): void {
    teams = net.teams_setting();
    const players = members(true);
    standings.hidden = net.member_count() === 0;
    standings.replaceChildren(
      el('div', { className: 'head' }, el('h3', { textContent: `Players ${players.length}/${net.max_players()}` })),
      ...(teams > 0 ? teamRows() : players),
      ...spectators(),
    );
  }

  /** Standings row per tank, for greying out the dead ones each frame. */
  let standingRows: HTMLElement[] = [];

  /** Place, name and match points of every tank, then the spectators, left of the map during a match. */
  function showStandings(): void {
    const totals = net.totals();
    standings.hidden = net.round_number() === 0 || totals.length === 0;
    if (standings.hidden) return;
    const bounty = net.bounty();
    const order = [...totals.keys()].sort((a, b) => totals[b] - totals[a] || a - b);
    standingRows = [];
    const row = (i: number) =>
      (standingRows[i] = el(
        'div',
        { className: i === net.you() ? 'row you' : 'row' },
        el('span', { className: 'rank', textContent: `${1 + totals.filter((t) => t > totals[i]).length}.` }),
        el('span', { className: 'name' }, tankMark(i), tankName(i)),
        i === bounty && el('span', { className: 'bounty', textContent: '💰', title: 'Bounty: +1 per hit, at most 3 per round' }),
        el('span', { className: 'pts', textContent: String(totals[i]) }),
      ));
    // Teams (Design §12): the team with the most rounds won first, that number in the heading.
    const wins = net.team_wins();
    const byTeam = [...wins.keys()].filter((t) => order.some((i) => tankData.teams[i] === t)).sort((a, b) => wins[b] - wins[a] || a - b);
    standings.replaceChildren(
      el(
        'div',
        { className: 'head' },
        el('h3', { textContent: '🏆 Standings' }),
        el('div', { className: 'sub', textContent: net.match_wins() ? `Round ${net.round_number()} · first to ${net.match_wins()} wins` : `Round ${net.round_number()} of ${net.round_count()}` }),
      ),
      ...(tankData.teams.length
        ? byTeam.flatMap((t) => [teamHead(t, '', el('span', { className: 'wins', textContent: roundsWon(wins[t]) })), ...order.filter((i) => tankData.teams[i] === t).map(row)])
        : order.map(row)),
      ...spectators(),
    );
  }

  const roundsWon = (n: number) => `${n}/${net.match_wins()} wins`;

  /**
   * Greys out the dead tanks in the standings, reds the living ones whose player has lost the
   * connection (not with anonymous colours: it would tell who is who).
   */
  function markDead(tanks: Float32Array): void {
    const away = new Set<number>();
    for (let m = 0; m < net.member_count(); m++) {
      if (net.member_slot(m) >= 0 && !net.member_connected(m) && !net.member_bot(m)) away.add(net.member_slot(m));
    }
    standingRows.forEach((row, i) => {
      const dead = !tankData.anonymous && tanks[i * consts.tankStride + 4] !== 1;
      const gone = !tankData.anonymous && !dead && away.has(i);
      if (row.classList.contains('dead') !== dead) row.classList.toggle('dead', dead);
      if (row.classList.contains('away') !== gone) row.classList.toggle('away', gone);
    });
  }

  /** Round result, or the final standings at match end. */
  function showBoard(): void {
    const places = net.places();
    const placePoints = net.place_points();
    const kills = net.kills();
    const bounty = net.bounty_points();
    const spent = net.spent();
    const totals = net.totals();
    // At match end the server's final places, a points tie broken by place, then kill points.
    const ranks = net.match_over() ? net.match_ranks() : [];
    const rankOf = (i: number) => ranks[i] ?? 1 + totals.filter((t) => t > totals[i]).length;
    const order = [...totals.keys()].sort((a, b) => rankOf(a) - rankOf(b) || places[a] - places[b]);
    const leaders = order.filter((i) => rankOf(i) === 1);
    // With teams the team(s) with the most rounds won win the match (Design §12).
    const teams = tankData.teams;
    const wins = net.team_wins();
    const best = Math.max(0, ...wins);
    const winners = teams.length ? new Set([...wins.keys()].filter((t) => wins[t] === best)) : new Set(leaders);
    const sideOf = (i: number) => (teams.length ? teams[i] : i);
    const youWin = net.you() >= 0 && winners.size === 1 && winners.has(sideOf(net.you()));
    // Once per round end: won (a tank of yours or your team's left) or not; at the match end
    // the fanfare for every winner, also on a tie.
    const heard = `${net.round_number()}:${net.match_over()}`;
    if (net.you() >= 0 && heard !== resultHeard) {
      resultHeard = heard;
      const mine = (i: number) => sideOf(i) === sideOf(net.you());
      if (net.match_over()) sound(winners.has(sideOf(net.you())) ? 'matchWin' : 'roundLose');
      else sound(places.some((p, i) => p === 1 && mine(i)) ? 'roundWin' : 'roundLose');
    }
    // The match winner strikes their pose; with teams everybody of the winning teams, else
    // never on a tie.
    const posing = (i: number) => net.match_over() && winners.has(sideOf(i)) && (teams.length > 0 || winners.size === 1);

    let title = net.match_wins() ? `Round ${net.round_number()} · first to ${net.match_wins()} wins` : `Round ${net.round_number()}/${net.round_count()}`;
    if (net.match_over() && teams.length) {
      const names = [...winners].map((t) => TEAM_NAMES[t]);
      title = winners.size > 1 ? `Match over – tie: ${names.join(', ')} win!` : youWin ? 'Match over – your team wins!' : `Match over – Team ${names[0]} wins!`;
    } else if (net.match_over()) {
      title = winners.size > 1 ? 'Match over – tie!' : youWin ? 'Match over – you win!' : `Match over – ${tankName(leaders[0])} wins!`;
    }
    const teamMark = (i: number) => {
      if (!teams.length) return '';
      const mark = el('span', { className: 'team-mark', textContent: '▌' });
      mark.style.color = TEAM_COLORS[teams[i]];
      return mark;
    };
    const rows = order.map((i) => {
      const rank = rankOf(i);
      return el(
        'tr',
        { className: i === net.you() ? 'you' : '' },
        el('td', {}, `${rank}. `, teamMark(i), tankMark(i), tankName(i), posing(i) ? pose(i) : ''),
        el('td', { textContent: `${places[i]}.` }),
        el('td', { textContent: `+${placePoints[i] ?? 0}` }),
        el('td', { textContent: (kills[i] ?? 0) < 0 ? `−${-kills[i]}` : `+${kills[i] ?? 0}` }),
        !teams.length && el('td', { textContent: `+${bounty[i] ?? 0}` }),
        net.match_over() && el('td', { textContent: spent[i] ? `−${spent[i]}` : '0' }),
        el('td', { textContent: `${totals[i]}` }),
      );
    });
    const head = el(
      'tr',
      {},
      ...['Player', 'Round place', 'Place points', 'Kill points', ...(teams.length ? [] : ['Bounty']), ...(net.match_over() ? ['Shop'] : []), 'Total'].map((t) =>
        el('th', { textContent: t }),
      ),
    );
    const close = el('button', {
      className: 'close',
      textContent: '✕',
      title: 'Close',
      onclick: () => (board.hidden = true),
    });
    board.classList.toggle('final', net.match_over());
    board.replaceChildren(
      net.match_over() ? close : '',
      el('h2', { textContent: title }),
      el('table', {}, head, ...rows),
      net.match_over() ? rewardLine() : '',
    );
    board.hidden = false;
  }

  /**
   * The victory pose of tank `i` behind its name (Design §11.2), none if it wears none: the
   * tank level with the name, the effect free to reach over the rows and headings above.
   */
  function pose(i: number): HTMLElement | string {
    const looks = net.looks();
    const id = looks[i * LOOK_SLOTS + POSE] ?? 0;
    if (id === 0) return '';
    const canvas = el('canvas');
    const [w, h, dpr] = [POSE_PX, (POSE_PX * POSE_H) / POSE_W, window.devicePixelRatio || 1];
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.style.top = `calc(50% - ${(h * POSE_TANK_Y) / POSE_H}px)`;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const spot = el('span', { className: 'pose' }, canvas);
    spot.style.width = `${w}px`;
    const ctx = canvas.getContext('2d')!;
    const parts = net.loadouts().slice(i * 4, i * 4 + 4);
    const color = tankColor(i);
    const frame = (now: number) => {
      if (!canvas.isConnected || board.hidden) return; // a new board draws a new one
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawPose(ctx, id, canvas.width, color, consts, looks[i * LOOK_SLOTS + PAINT], parts, now, looks[i * LOOK_SLOTS + COLOR]);
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
    return spot;
  }

  /** Whether you are here as a guest. */
  function ownGuest(): boolean {
    for (let i = 0; i < net.member_count(); i++) {
      if (net.member_id(i) === net.player()) return net.member_guest(i);
    }
    return true;
  }

  /** Own reward points of the finished match, and of the daily challenge done in it (Design
   * §11.1, right under it); guests only see what they missed. */
  function rewardLine(): HTMLElement | string {
    const reward = net.match_rewards()[net.you()] ?? 0;
    const challenge = net.match_challenge()[net.you()] ?? 0;
    const lines: HTMLElement[] = [];
    if (reward > 0) {
      lines.push(
        ownGuest()
          ? el('p', { className: 'hint', textContent: `With an account you would have earned ${reward} reward points.` })
          : el('p', { className: 'reward', textContent: `+${reward} reward points` }),
      );
    }
    if (challenge > 0) lines.push(el('p', { className: 'reward', textContent: `+${challenge} reward points · daily challenge` }));
    return lines.length ? el('div', {}, ...lines) : '';
  }

  let updateStart: (() => void) | undefined; // start button countdown after a match

  /** Teams with a seated member (2 without teams: nothing to wait for). */
  function teamsUsed(): number {
    if (net.teams_setting() === 0) return 2;
    const used = new Set<number>();
    for (let i = 0; i < net.member_count(); i++) if (net.member_slot(i) >= 0) used.add(net.member_team(i));
    return used.size;
  }

  /** Settings and buttons between matches; the members are left of the map. */
  function showPanel(): void {
    updateStart = undefined;
    panel.hidden = net.playing();
    if (panel.hidden) return;
    const host = net.is_host();
    const me = net.player();
    const max = net.max_players();

    let players = 0;
    let mySlot = -1;
    for (let i = 0; i < net.member_count(); i++) {
      if (net.member_slot(i) >= 0) players++;
      if (net.member_id(i) === me) mySlot = net.member_slot(i);
    }

    const select = (options: [number, string][], value: number) => {
      const s = el('select', { disabled: !host });
      s.append(...options.map(([v, t]) => el('option', { value: String(v), textContent: t, selected: v === value })));
      s.onchange = sendSettings;
      return s;
    };
    const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
    const maxSel = select(
      range(2, MAX_PLAYERS).map((n) => [n, String(n)]),
      max,
    );
    const roundsSel = select([[0, 'Auto'], ...range(1, MAX_ROUNDS).map((n): [number, string] => [n, String(n)])], net.rounds_setting());
    // With teams the match goes to round wins instead (Design §12).
    const winsSel = select([[0, 'Auto'], ...range(2, MAX_WINS).map((n): [number, string] => [n, String(n)])], net.wins_setting());
    // Few choices: a row of keys, the chosen one pressed.
    const chosen = {
      visible: Number(net.is_public()),
      powerups: Number(net.powerups_setting()),
      guests: Number(net.guests_setting()),
      anonymous: Number(net.anonymous_setting()),
      fog: net.fog_setting(),
      seasons: net.seasons_setting(),
      terrain: net.terrain_setting(),
      bots: net.bots_setting(),
      level: net.bot_level_setting(),
      teams: net.teams_setting(),
    };
    const seg = (key: keyof typeof chosen, options: [number, string][]) =>
      el(
        'div',
        { className: 'seg' },
        ...options.map(([v, t]) =>
          el('button', {
            textContent: t,
            className: v === chosen[key] ? 'on' : '',
            disabled: !host,
            onclick: () => {
              chosen[key] = v;
              sendSettings();
            },
          }),
        ),
      );
    const onOff = (on: string, off: string): [number, string][] => [
      [1, on],
      [0, off],
    ];
    // A row of keys; a server allowing more (tests at home) gets a list.
    const botsChoice = () => {
      const counts = range(0, net.max_bots()).map((n): [number, string] => [n, String(n)]);
      if (counts.length <= 6) return seg('bots', counts);
      const s = select(counts, chosen.bots);
      s.onchange = () => {
        chosen.bots = Number(s.value);
        sendSettings();
      };
      return s;
    };
    function sendSettings(): void {
      const c = chosen;
      send(net.settings(Number(maxSel.value), c.visible === 1, Number(roundsSel.value), c.powerups, c.guests === 1, c.anonymous === 1, c.bots, c.level, c.fog, c.seasons, c.terrain, c.teams, Number(winsSel.value)));
    }

    const link = `${location.origin}${location.pathname}?lobby=${code}`;
    const linkInput = el('input', { value: link, readOnly: true });
    const copy = el('button', { textContent: 'Copy' });
    copy.onclick = async () => {
      try {
        await navigator.clipboard.writeText(link); // needs HTTPS or localhost
        copy.textContent = 'Copied ✓';
      } catch {
        linkInput.select();
      }
    };

    let seat: HTMLElement | string = ''; // the host always plays
    if (mySlot < 0) {
      seat = el('button', {
        className: 'big',
        textContent: 'Play',
        disabled: players >= max,
        title: players >= max ? 'All seats taken' : '',
        onclick: () => send(net.spectate(false)),
      });
    } else if (!host) {
      seat = el('button', { className: 'big', textContent: 'Watch', onclick: () => send(net.spectate(true)) });
    }
    let start: HTMLElement | string = seat; // non-hosts: their seat button in its place
    if (host) {
      const button = el('button', { className: 'primary big', onclick: () => send(net.start()) });
      updateStart = () => {
        const wait = net.start_wait_secs(performance.now());
        const oneTeam = teamsUsed() < 2;
        patch(button, {
          disabled: players < 2 || oneTeam || wait > 0,
          textContent: wait > 0 ? `Start match (${wait} s)` : 'Start match',
          title: players < 2 ? 'At least 2 players' : oneTeam ? 'Players needed on at least two teams' : wait > 0 ? 'Everyone is still viewing the match results' : '',
        });
      };
      updateStart();
      start = button;
    }

    const label = (text: string, title = '') => el('span', { textContent: text, title });
    panel.replaceChildren(
      el('div', { className: 'lobby-head' }, el('h2', { textContent: 'Lobby' }), el('span', { className: 'lobby-code', textContent: code })),
      el('div', { className: 'stack' }, start, el('div', { className: 'link' }, linkInput, copy)),
      // Grouped, the bots at the bottom (Wunsch User 2026-10-13). A guest host gets only the
      // rows marked for guests, the rest stays standard (Design §9.3).
      ...settingGroups([
        [
          'Lobby',
          [
            [label('Max. players'), maxSel, true],
            [label('Visibility', 'Private: link only'), seg('visible', onOff('public', 'private')), true],
            [label('Guests'), seg('guests', onOff('allowed', 'accounts only')), false],
          ],
        ],
        [
          'Match',
          [
            net.teams_setting()
              ? [label('Wins', 'The first team to win this many rounds takes the match; at most this many rounds per team are played, then the most wins win'), winsSel, true]
              : [label('Rounds'), roundsSel, true],
            [label('Power-ups'), seg('powerups', [[1, 'on'], [2, 'hidden'], [0, 'off']]), false],
            [label('Anonymous colors', 'All tanks gray, without names and without paint/bullet cosmetics'), seg('anonymous', onOff('on', 'off')), true],
            [
              label('Teams', 'Last team standing wins the round; friendly fire is on, a team kill costs 1 point; pick your team in the player list'),
              seg('teams', [
                [0, 'off'],
                [2, '2'],
                [3, '3'],
                [4, '4'],
              ]),
              true,
            ],
          ],
        ],
        [
          'Terrain & vision',
          [
            [
              label(
                'Seasons',
                'Terrain per round: Spring (fog banks, rain showers, hedges, stream), Summer (sand, heat haze, tall grass, pond), Autumn (mud, leaves, puddles, gusts of wind), Winter (ice, deep snow, frozen lake, snowfall, blizzard); hills in all of them',
              ),
              seg('seasons', [
                [0, 'off'],
                [1, 'random'],
                [2, 'in turn'],
              ]),
              false,
            ],
            [
              label('Terrain effects', 'How many of the season\'s effects a round has'),
              seg('terrain', [
                [1, '1'],
                [2, '2'],
                [0, 'all'],
              ]),
              false,
            ],
            [
              label(
                'Fog',
                'What your tank does not see lies under a gray veil; enemies, bullets and other tanks\' mines there are invisible · Obstacles: they cast shadows · On: in addition only a view cone to the front (width per cannon) and 2 tank lengths all around',
              ),
              seg('fog', [
                [0, 'off'],
                [1, 'obstacles'],
                [2, 'on'],
              ]),
              false,
            ],
          ],
        ],
        [
          'Bots',
          [
            [
              label('Count', 'Computer opponents on free seats; new players replace them'),
              botsChoice(),
              false,
            ],
            [
              label(
                'Skill',
                'Easy: circle an obstacle and shoot aimlessly · Medium: keep their distance, zigzag, aim inaccurately · Hard: drive straight at you and hit precisely',
              ),
              seg('level', [
                [0, 'easy'],
                [1, 'medium'],
                [2, 'hard'],
              ]),
              false,
            ],
          ],
        ],
      ]),
    );
  }

  /**
   * Lobby settings in titled groups: per group its rows (label, control, shown to a guest
   * host); a guest host sees only the guest rows, groups without any are left out.
   */
  function settingGroups(groups: [string, [HTMLElement, HTMLElement, boolean][]][]): HTMLElement[] {
    const guestHost = net.is_host() && ownGuest();
    const shown = groups.flatMap(([title, rows]) => {
      const shown = rows.filter(([, , guest]) => guest || !guestHost);
      if (!shown.length) return [];
      return [el('div', { className: 'group' }, el('h3', { textContent: title }), el('div', { className: 'settings' }, ...shown.flatMap(([l, c]) => [l, c])))];
    });
    // The rest is for accounts: say so at the end.
    if (guestHost) shown.push(el('p', { className: 'hint guest-more', textContent: 'Sign up for more settings: power-ups, bots, seasons, terrain, fog and who may join.' }));
    return shown;
  }

  /** Whether another person still plays in the match: leaving against bots only needs no asking. */
  function playsWithPeople(): boolean {
    for (let i = 0; i < net.member_count(); i++) {
      if (net.member_slot(i) >= 0 && !net.member_bot(i) && net.member_connected(i) && net.member_id(i) !== net.player()) return true;
    }
    return false;
  }

  leave.onclick = async () => {
    const asks = net.playing() && net.you() >= 0 && playsWithPeople();
    if (asks && !(await ask('Leave the match? Your tank stays where it is.', 'Leave', 'Stay'))) return;
    closed = true;
    ws?.close();
    backToMenu();
  };

  function roundInfo(alive: boolean): string {
    if (!net.has_round()) return '';
    const you = net.you();
    if (net.round_number() === 0) {
      if (you < 0) return 'Warm-up';
      return net.is_host() ? 'Warm-up · Host' : 'Warm-up · waiting';
    }
    const round = net.match_wins() ? `${net.round_number()} · to ${net.match_wins()} wins` : `${net.round_number()}/${net.round_count()}`;
    if (you < 0) return `${round} · from next match`;
    return alive ? round : `${round} · out`;
  }

  /** Emotes (stride: tank, emote, age) but the others' when hidden. */
  const shownEmotes = (all: Float32Array, you: number): Float32Array =>
    all.filter((_, k, a) => showsEmotesOf(a[k - (k % 3)], you));

  /** The ghost if it is `you`'s, else none. */
  const ghostOf = (you: number): Float32Array => {
    const g = net.ghost();
    return g.length && g[6] === you ? g : new Float32Array();
  };

  /** A ghost's piece by its kind (`terrain_patches` numbering) and its time to place it. */
  const GHOST_PIECES: Record<number, string> = { 0: 'ice', 4: 'a puddle', 7: 'a hedge', 9: 'sand' };
  const GHOST_SECS = 10;

  /** Status bar; `tanks` as drawn this frame (none without a round). */
  function updateStatus(tanks: Float32Array | undefined): void {
    const you = net.you();
    let [item, alive] = ['', true];
    if (you >= 0 && tanks) {
      const o = you * consts.tankStride;
      alive = tanks[o + 4] > 0;
      item = itemInfo(tanks, o);
    }
    setItem(item || '–');
    setPing(connection ? '' : `${Math.round(net.rtt())} ms`);
    if (connection) return setRound(connection);
    // Your ghost: how to place its piece and how long you still may (Design §6.3).
    const ghost = net.ghost();
    if (ghost.length && ghost[6] === you) {
      const piece = GHOST_PIECES[ghost[3]] ?? 'piece';
      return setRound(`Ghost: move, Fire places ${piece} (${Math.ceil(ghost[5] * GHOST_SECS)} s)`, '#c9c2ff');
    }
    // Names in their colour, not in anonymous rounds: out by whose hand, and whom you got last.
    const named = !tankData.anonymous;
    const killer = alive ? -1 : net.killer();
    const tail: [string, string][] = [];
    if (killer >= 0 && killer !== you && named) tail.push([' · by ', ''], [tankName(killer), tankColor(killer)]);
    const lastVictim = net.last_victim();
    if (lastVictim >= 0 && named) tail.push([' · killed ', ''], [tankName(lastVictim), tankColor(lastVictim)]);
    setRound(killer === you ? `${roundInfo(alive)} · own shot` : roundInfo(alive), '', tail);
  }


  // Emotes (Design §11.4): keys 1–4, the touch controller's 💬, on a controller Y/△ picks (a
  // faint bubble shows which) and X/□ sends.
  let emotePick = -1;
  let emotePickAt = 0;
  const EMOTE_PICK_MS = 3000;
  // The emotes on the own keys, as chosen in the cosmetics shop when joining (guests: the free ones).
  let emoteKeys = STANDARD_KEYS;
  void profile()
    .then((me) => {
      if (me?.emotes) [emoteKeys] = [me.emotes];
      setTouchEmotes(emoteKeys);
    })
    .catch(() => {});
  const sendEmote = (index: number) => {
    if (isOpen() && net.has_round() && net.you() >= 0) send(net.emote(index));
    emotePick = -1;
  };
  onTouchEmote(sendEmote);
  onPadEmote('y', () => {
    emotePick = (emotePick + 1) % emoteKeys.length;
    emotePickAt = performance.now();
  });
  onPadEmote('x', () => {
    if (emotePick >= 0) sendEmote(emotePick);
  });

  // Leaving the tab: release all keys on the server, too.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) send(net.input(0, 0, false, false));
  });
  setInterval(() => send(net.ping(performance.now())), PING_INTERVAL_MS);

  let lastTime = 0;
  let acc = 0;

  function loop(now: number): void {
    const elapsed = lastTime ? Math.min((now - lastTime) / 1000, 0.25) : 0;
    lastTime = now;
    acc += elapsed;

    while (acc >= dt) {
      if (isOpen() && net.has_round() && !net.frozen()) {
        // Behind round result and shop the round stands still.
        const input = readAnyInput();
        send(net.input(input.throttle, input.turn, input.fire, input.item));
      }
      acc -= dt;
    }

    const t = performance.now();
    const tanks = net.has_round() ? net.tanks(t, acc / dt) : undefined;
    for (let k = 0; k < emoteKeys.length; k++) if (takePress(`Digit${k + 1}`)) sendEmote(k);
    if (emotePick >= 0 && t - emotePickAt > EMOTE_PICK_MS) emotePick = -1;
    // A ghost placed a piece of ground: the floor anew.
    if (net.terrain_stamp() !== terrainStamp) [terrain, terrainStamp] = [net.terrain_patches(), net.terrain_stamp()];
    // Touch screens: the on-screen controller while the own tank plays; they and small windows
    // show only the map during a match round (Wunsch User 2026-10-26).
    setTouchActive(isOpen() && net.has_round() && !net.frozen() && net.you() >= 0);
    const small = innerWidth < FILL_BELOW.width || innerHeight < FILL_BELOW.height;
    const fill = (isTouchMode() || small) && net.round_number() > 0 && net.has_round() && !net.frozen();
    if (document.body.classList.contains('fill') !== fill) document.body.classList.toggle('fill', fill);
    const right = panel.hidden ? 0 : panel.offsetWidth + 2 * PANEL_GAP;
    setTouchRight(fill ? 0 : right);
    if (tanks) {
      const countdown = net.countdown(t);
      if (net.round_number() > 0) markDead(tanks);
      countdownSound(net.round_number() > 0 ? countdown : 0);
      // Find your tank during the countdown; afterwards you know it (none in the warm-up).
      const you = net.you();
      const ring = net.round_number() > 0 && countdown > 0 ? you : -1;
      const aimPaths: Float32Array[] = [];
      if (you >= 0) aimPaths[you] = net.aim_path(acc / dt);
      const map = draw(
        ctx,
        {
          worldWidth: net.world_width(),
          worldHeight: net.world_height(),
          walls,
          barriers: net.barriers(),
          tanks,
          bullets: net.bullets(t),
          blasts: net.blasts(t),
          ring,
          zone: net.zone(t),
          countdown,
          names: tankData.names,
          pickups: net.pickups(),
          mines: net.mines(),
          oil: net.oil(),
          aimPaths,
          you,
          looks: tankData.looks,
          loadouts: tankData.loadouts,
          colors: tankData.colors,
          teams: tankData.teams,
          // Rank badges only in the warm-up (Design §11.3); anonymous matches never get there.
          badges: net.round_number() === 0 ? tankData.ranks : undefined,
          anonymous: tankData.anonymous,
          fog: tankData.fog,
          season: net.season(),
          terrainEffects: net.terrain_effects(),
          terrain,
          holes: net.holes(),
          smoke: net.smoke(),
          // Only the ghost sees itself: where the piece comes is a surprise (Wunsch User 2026-10-26).
          ghost: ghostOf(net.you()),
          emotes: shownEmotes(net.emotes(t), net.you()),
          emotePreview: emotePick >= 0 ? emoteKeys[emotePick] : -1,
          wind: Float32Array.from(net.wind(t)),
          raining: net.raining(t),
          fogBanks: net.fog_banks(t),
          effects,
          bounty: net.round_number() > 0 ? net.bounty() : -1,
          left: standings.hidden ? 0 : standings.offsetWidth + 2 * STANDINGS_GAP,
          right,
          fill,
        },
        consts,
      );
      if (!standings.hidden) {
        // Centred between the window edge and the map, a little below the map's top.
        patch(standings.style, {
          left: `${Math.max(STANDINGS_GAP, (map.x - standings.offsetWidth) / 2)}px`,
          top: `${map.y + STANDINGS_TOP}px`,
        });
      }
    }
    if (shopTimer) patch(shopTimer, { textContent: String(net.shop_seconds(t)) });
    if (!panel.hidden) updateStart?.();
    updateStatus(tanks);
    countFrame(now);
    requestAnimationFrame(loop);
  }

  connect();
  requestAnimationFrame(loop);
}
